import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Loader } from 'common/react/components/Loader';
import { EmptyState } from 'common/react/components/EmptyState';
import { useGetDraftByYearQuery } from './api/draft-tracker-api';
import { DraftTrackerTable } from './components/DraftTrackerTable';
import { DraftTrackerHeader } from './components/DraftTrackerHeader';
import { DraftTrackerMobileTable } from './components/DraftTrackerMobileTable';
import { useBreakpoints } from 'react-breakpoints-hook';
import { useSelector } from 'react-redux';

export const DraftTrackerEmbeddable = ({ ...data }) => {
    const { linkTo, linkToLabel, config } = data;

    const { isLinkInNewTab, seasonToShow, numberOfPlayers, promo } =
        JSON.parse(config);

    const isWebView =
        PULSE.core.url.getParam('webview') === 'true'
            ? true
            : window.IS_ARTICLE_API === true;

    const [dataPolling, setDataPolling] = useState(
        PULSE.app.common.CONSTANTS.MATCH_POLL_DELAY
    );
    const token = useSelector(PULSE.app.redux.selectors.token.selectToken);
    //Polling is only needed when data is live

    const { data: draftData } = useGetDraftByYearQuery(seasonToShow, {
        pollingInterval: dataPolling,
        skip: !token ? true : false
    });
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [displayedRows, setDisplayedRows] = useState([]);

    useEffect(() => {
        // Set no data available to show the empty state
        if (draftData?.status && !isNaN(draftData.status)) {
            setNoDataAvailable(true);
        } else {
            setNoDataAvailable(false);
        }
        if (draftData?.selections?.length) {
            const lastPick = draftData?.selections.length
                ? draftData.selections[draftData.selections.length - 1]
                : null;
            if (lastPick?.fullName || lastPick?.passed) {
                setDataPolling(0);
            }
        }
    }, [draftData, draftData?.status]);

    useMemo(() => {
        if (draftData?.selections.length) {
            const selections = draftData.selections.filter(
                (selection) => selection.teamId === PULSE.envPaths.clubId
            );
            setDisplayedRows(selections.slice(0, numberOfPlayers));
        }
    }, [draftData?.selections]);

    let { lg } = useBreakpoints({
        lg: { min: 1025, max: null }
    });

    if (!draftData) {
        return <></>;
    }

    return draftData.status !==
        PULSE.app.common.CONSTANTS.DRAFT_STATUS.PRE_DRAFT ? (
        <div
            className={`draft-tracker-embeddable ${
                isWebView ? 'draft-embeddable--static-dimensions' : ''
            }`}
        >
            <DraftTrackerHeader
                title={linkToLabel}
                linkTo={linkTo}
                isLinkInNewTab={JSON.parse(isLinkInNewTab)}
                promo={promo}
            />

            {!draftData?.selections && !noDataAvailable && (
                <div className="draft-embeddable__loader">
                    <Loader />
                </div>
            )}

            {noDataAvailable && (
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation="label.stats.error.noStatsAvailable.summary"
                />
            )}

            {draftData?.selections &&
                !noDataAvailable &&
                (lg ? (
                    <DraftTrackerTable rows={displayedRows} />
                ) : (
                    <DraftTrackerMobileTable rows={displayedRows} />
                ))}
        </div>
    ) : (
        ''
    );
};

DraftTrackerEmbeddable.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    linkTo: PropTypes.string,
    linkToLabel: PropTypes.string,
    seasonToShow: PropTypes.string.isRequired,
    numberOfPlayers: PropTypes.string.isRequired
};
